export const instagramUrl = 'https://www.instagram.com/bright_digital_hubonline/'
export const checkoutUrl = 'https://complete-lumbar-rehabilitation-bundle.impultienda.ar/checkout'
export const offerAnchor = '#offer'
export const contactEmail = 'compradigitalyaa@gmail.com'

export const productName = 'Complete Lumbar Rehabilitation Bundle'

export const mockupUrl =
  'https://cdn.magicpatterns.com/uploads/dGTFHHAG6ZywG9AxLaASQW/Caja_Premium_de_Rehabilitacion_Lumbar.png'

export const bodyAreas = ['Movement Control', 'Mobility', 'Strength & Load', 'Functional Rehab', 'Patient Programs']

export const organizedNeeds = [
  { question: 'Need introductory movement-control exercises?', answer: "They're included." },
  { question: 'Need lumbar and hip mobility routines?', answer: "They're organized." },
  { question: 'Need strengthening exercises with sets, reps, and resistance progressions?', answer: "They're covered." },
  { question: 'Need functional activities for lifting, reaching, and returning to everyday tasks?', answer: "They're ready to adapt." },
  { question: 'Need complete programs for different patient needs?', answer: "They're already structured." },
]

export const systemFlow = ['Select the goal', 'Choose the exercise', 'Set the dosage', 'Adapt to your patient']

export const showcaseFeatures = [
  '80 detailed exercises',
  '48 structured rehabilitation sessions',
  '12 individualized patient-based programs',
  'Step-by-step execution instructions',
  'Sets, repetitions, and rest periods',
  'Exercise regressions and progressions',
  'Strength and resistance guidance',
  'Functional rehabilitation activities',
  'Five additional practical bonuses',
  'Instant digital access',
]

export type Volume = {
  number: string
  name: string
  count: string
  intro: string
  itemsLabel: string
  items: string[]
  sessions: string
  tagline: string
}

export const volumes: Volume[] = [
  {
    number: '01',
    name: 'Initial Exercise Selection & Programming',
    count: '20 exercises + 12 rehabilitation sessions',
    intro: 'A practical starting point for selecting exercises and organizing initial lumbar rehabilitation sessions.',
    itemsLabel: 'Includes:',
    items: ['Supine Pelvic Tilt', 'Supine Marching', 'Glute Bridge', 'Abdominal Bracing', 'Short-Lever Bird Dog', 'Supported Sit-to-Stand', 'Supported Hip Hinge', 'Graded Walking'],
    sessions: 'Plus complete sessions for introductory movement, trunk control, hip activation, and functional strength.',
    tagline: 'Select. Position. Program.',
  },
  {
    number: '02',
    name: 'Lumbar Mobility & Movement Control',
    count: '20 exercises + 12 rehabilitation sessions',
    intro: 'Practical exercises for lumbar movement, hip mobility, trunk rotation, and movement coordination.',
    itemsLabel: 'Includes:',
    items: ['Seated Forward Reach', 'Side-Lying Open Book', 'Prone Press-Up', 'Half-Kneeling Hip Flexor Mobility', 'Supine Hamstring Mobility', 'Supported 90/90 Hip Rotation', 'Standing Trunk Rotation', 'Functional Multidirectional Reach'],
    sessions: 'Plus structured sessions for mobility, movement control, and functional reaching.',
    tagline: 'Move. Control. Adapt.',
  },
  {
    number: '03',
    name: 'Strength & Load Progression',
    count: '20 exercises + 12 rehabilitation sessions',
    intro: 'Strength exercises with resistance selection, sets, repetitions, rest periods, and progressive loading options.',
    itemsLabel: 'Includes:',
    items: ['Modified Side Plank', 'Forearm Plank', 'Dead Bug', 'Pallof Press', 'Dumbbell Romanian Deadlift', 'Goblet Box Squat', 'Supported Split Squat', 'Farmer Carry'],
    sessions: 'Plus sessions for trunk endurance, posterior-chain strength, lower-body resistance, and progressive loading.',
    tagline: 'Strengthen. Load. Progress.',
  },
  {
    number: '04',
    name: 'Functional Rehabilitation & Return to Activity',
    count: '20 exercises + 12 rehabilitation sessions',
    intro: 'Practical exercises for daily movement and progressively more demanding functional activities.',
    itemsLabel: 'Includes:',
    items: ['Bed Mobility and Transfers', 'Chair-to-Chair Transfers', 'Low-Shelf Object Retrieval', 'Floor-to-Waist Object Lifting', 'Laundry Basket Transfers', 'Functional Pushing and Pulling', 'Stair Negotiation', 'Multi-Height Object Handling'],
    sessions: 'Plus structured sessions for household tasks, lifting, transfers, and return-to-activity preparation.',
    tagline: 'Practice. Move. Return to Activity.',
  },
  {
    number: '05',
    name: 'Individualized Session Programming & Patient Progress Tracking',
    count: '12 patient-based programs + 8 programming scenarios',
    intro: 'Complete exercise-programming examples for different functional limitations and rehabilitation goals.',
    itemsLabel: 'Includes programs for:',
    items: ['Limited sitting and standing tolerance', 'Difficulty bending', 'Reduced trunk endurance', 'Limited hip mobility', 'Lower-body strengthening', 'Walking capacity', 'Household activities', 'Recreational exercise preparation', 'Physical work task preparation'],
    sessions: 'With weekly plans, exercise dosage, modifications, and follow-up decisions.',
    tagline: 'Assess. Customize. Monitor.',
  },
]

export const sampleMeta = [
  ['Main goal', 'Introductory lumbar movement control and functional strengthening'],
  ['Main reference', 'Volume 01'],
  ['Selected exercises', 'Supine Pelvic Tilt, Supine Marching, Glute Bridge, Supported Sit-to-Stand'],
] as const

export const sampleTimeline = [
  ["Review the patient's functional goal", '4 min'],
  ['Supine Pelvic Tilt — 2 × 10', '4 min'],
  ['Supine Marching — 2 × 8/side', '5 min'],
  ['Glute Bridge — 2 × 10', '6 min'],
  ['Supported Sit-to-Stand — 2 × 8', '7 min'],
  ['Functional reassessment and documentation', '4 min'],
] as const

export const protocolStructure = [
  'Starting position',
  'Step-by-step execution',
  'Sets and repetitions',
  'Rest periods',
  'Exercise modifications',
  'Regressions and progressions',
  'Relevant precautions',
]

export const differentiators = [
  {
    icon: 'book',
    title: 'Step-by-Step Exercises',
    text: '80 exercises with positioning, execution instructions, sets, repetitions, and exercise modifications.',
  },
  {
    icon: 'calendar',
    title: 'Structured Clinical Sessions',
    text: '48 ready-to-adapt rehabilitation sessions covering mobility, strength, movement control, and functional activities.',
  },
  {
    icon: 'activity',
    title: 'Progressive Rehabilitation',
    text: 'Practical tools for adapting resistance, movement difficulty, repetitions, and functional demands.',
  },
  {
    icon: 'clock',
    title: 'Less Preparation Time',
    text: 'Exercises, complete sessions, home programs, and professional tools organized in one collection.',
  },
] as const
